import { API_BASE_URL } from './api'

/**
 * Genera el path de la miniatura a partir del path original
 * @param {string} imageUrl - URL original de la imagen
 * @param {string} suffix - Sufijo del tamaño ('thumb' o 'card', por defecto 'thumb')
 * @returns {string} Path de la miniatura
 */
function getThumbnailPath(imageUrl, suffix = 'thumb') {
  if (!imageUrl) return ''
  
  // Extraer solo la parte del path después de /storage/
  let path = imageUrl
  
  // Si es una URL completa (http/https), extraer solo el pathname
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    try {
      const url = new URL(imageUrl)
      path = url.pathname
    } catch (e) {
      // Si falla el parsing, intentar extraer manualmente
      const match = imageUrl.match(/\/storage\/[^?#]*/)
      if (match) {
        path = match[0]
      }
    }
  }
  
  // Limpiar el path - remover /storage/ del inicio si existe
  path = path.replace(/^\/storage\//, '').replace(/^storage\//, '')
  
  // Si después de limpiar está vacío, devolver la URL original
  if (!path) {
    return imageUrl
  }
  
  // Extraer directorio, nombre y extensión
  const lastSlash = path.lastIndexOf('/')
  const directory = lastSlash >= 0 ? path.substring(0, lastSlash + 1) : ''
  const filename = lastSlash >= 0 ? path.substring(lastSlash + 1) : path
  
  const lastDot = filename.lastIndexOf('.')
  if (lastDot === -1) {
    // No hay extensión, devolver path original con /storage/
    return `/storage/${path}`
  }
  
  const name = filename.substring(0, lastDot)
  const extension = filename.substring(lastDot)
  
  // Crear nombre de miniatura: nombre_suffix.extension
  const thumbnailFilename = `${name}_${suffix}${extension}`
  const thumbnailPath = `/storage/${directory}${thumbnailFilename}`
  
  return thumbnailPath
}

/**
 * Obtiene la URL de la imagen, intentando usar la miniatura si está disponible
 * @param {string} imageUrl - URL original de la imagen
 * @param {boolean} useThumbnail - Si true, intenta usar la miniatura (por defecto: true)
 * @param {boolean} forceOriginal - Si true, fuerza el uso de la imagen original (para diálogos de imagen completa)
 * @param {string} size - Tamaño de la miniatura ('thumb' o 'card', por defecto 'thumb')
 * @returns {string} URL de la imagen (miniatura o original)
 */
export function getImageUrl(imageUrl, useThumbnail = true, forceOriginal = false, size = 'thumb') {
  if (!imageUrl) return ''
  let normalizedUrl = imageUrl
  
  // Si viene como URL absoluta del propio backend (API_BASE_URL + /storage/...), trátala como local
  if (normalizedUrl.startsWith(API_BASE_URL)) {
    try {
      const u = new URL(normalizedUrl)
      normalizedUrl = `${u.pathname}${u.search}${u.hash}`
    } catch {
      // ignore
    }
  }
  
  // Si se fuerza la original, usar directamente
  if (forceOriginal) {
    useThumbnail = false
  }
  
  // Si es una URL externa (http/https), devolverla tal cual
  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    return normalizedUrl
  }
  
  // Si es una URL de storage local y queremos usar miniatura
  if (useThumbnail && (normalizedUrl.startsWith('/storage/') || normalizedUrl.startsWith('storage/') || normalizedUrl.includes('/storage/'))) {
    const thumbnailPath = getThumbnailPath(normalizedUrl, size)
    // Asegurar que el path empiece con /storage/
    const cleanThumbnailPath = thumbnailPath.startsWith('/storage/') ? thumbnailPath : `/storage/${thumbnailPath.replace(/^\/?storage\//, '')}`
    // Devolver la URL de la miniatura (el navegador cargará la original si no existe)
    return `${API_BASE_URL}${cleanThumbnailPath}`
  }
  
  // Para URLs de storage, construir la URL completa
  if (normalizedUrl.startsWith('/storage/') || normalizedUrl.startsWith('storage/')) {
    const cleanPath = normalizedUrl.startsWith('/') ? normalizedUrl : `/${normalizedUrl}`
    return `${API_BASE_URL}${cleanPath}`
  }
  
  // Si es una ruta de API relativa
  if (normalizedUrl.startsWith('/api/')) {
    return `${API_BASE_URL}${normalizedUrl}`
  }
  
  // Por defecto, devolver la URL tal cual
  return normalizedUrl
}

/**
 * Obtiene la URL de la imagen original (sin miniatura)
 * Útil para diálogos de imagen completa
 * @param {string} imageUrl - URL de la imagen
 * @returns {string} URL de la imagen original
 */
export function getOriginalImageUrl(imageUrl) {
  return getImageUrl(imageUrl, false, true)
}

/**
 * Devuelve la URL en variante WebP para imágenes de reviews en storage.
 * Solo aplica a rutas que contienen /storage/reviews/ o /reviews/ (misma extensión .webp).
 * Útil para <picture><source type="image/webp"> en hero/cards de reviews.
 *
 * @param {string} imageUrl - URL de la imagen (p. ej. .../reviews/xxx.jpg?v=1)
 * @returns {string} URL con extensión .webp o '' si no es una imagen de review
 */
export function getReviewImageWebpUrl(imageUrl) {
  if (!imageUrl) return ''
  if (!imageUrl.includes('/storage/reviews/') && !imageUrl.includes('/reviews/')) return ''
  return imageUrl.replace(/\.(jpe?g|png|gif)(\?|#|$)/gi, '.webp$2')
}

/**
 * URL de la variante _medium (800px) para imágenes de reviews/body; permite srcset responsive.
 */
export function getReviewBodyImageMediumUrl(imageUrl) {
  if (!imageUrl) return ''
  if (!imageUrl.includes('/reviews/body/')) return ''
  return imageUrl.replace(/\.(jpe?g|png|gif)(\?|#|$)/gi, '_medium.$1$2')
}

/** URL de la variante _medium en WebP para imágenes de reviews/body. */
export function getReviewBodyImageMediumWebpUrl(imageUrl) {
  if (!imageUrl) return ''
  if (!imageUrl.includes('/reviews/body/')) return ''
  return imageUrl.replace(/\.(jpe?g|png|gif)(\?|#|$)/gi, '_medium.webp$2')
}

/** URL de la variante _small (400px) para imágenes de reviews/body. */
export function getReviewBodyImageSmallUrl(imageUrl) {
  if (!imageUrl) return ''
  if (!imageUrl.includes('/reviews/body/')) return ''
  return imageUrl.replace(/\.(jpe?g|png|gif)(\?|#|$)/gi, '_small.$1$2')
}

/** URL de la variante _small en WebP para imágenes de reviews/body. */
export function getReviewBodyImageSmallWebpUrl(imageUrl) {
  if (!imageUrl) return ''
  if (!imageUrl.includes('/reviews/body/')) return ''
  return imageUrl.replace(/\.(jpe?g|png|gif)(\?|#|$)/gi, '_small.webp$2')
}

